import React from "react";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FiCheck,
  FiArrowRight,
  FiUserPlus,
  FiUsers,
  FiAtSign,
  FiTarget,
  FiBriefcase,
  FiFileText,
  FiMessageSquare,
  FiSend,
  FiPlus,
} from "react-icons/fi";
import { fetchWithAuth } from "../utils/authFetch";
import { useAuth } from "../context/AuthContext";
import { formatCredits } from "../utils/useCredits";
import Skeleton from "../components/Skeleton";
import { PLANS, TRIAL_DAYS, usd, annualTotal, ANNUAL_DISCOUNT } from "./PricingPage";
import { DemoModal } from "./LandingPage";

/* Plans as seen from inside the app: pick a plan and a seat count here, see the price,
   then continue to add-ons and payment. Same prices as the public page. */

/* Fills the pane edge to edge (MainContent pads 2.5rem all round). The bottom padding keeps
   the last content clear of the fixed order bar. */
const Page = styled.main`
  width: 100%;
  padding-bottom: 104px;

  /* On a phone the order bar wraps to two or three rows. */
  @media (max-width: 450px) {
    padding-bottom: 200px;
  }
`;

/* The whole page is set at 90%: zoom scales type, spacing and the cards together while
   layout widths still fill the pane. The fixed bar keeps its own insets unscaled. */
const Scaled = styled.div`
  zoom: 0.9;
`;

const Head = styled.header`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 18px 24px;
  margin-bottom: 30px;

  h1 {
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 56px;
    font-weight: 400;
    line-height: 1;
    letter-spacing: -0.015em;
    margin: 0;
    color: ${({ theme }) => theme.ink};
  }
  h1 em {
    font-style: italic;
    color: ${({ theme }) => theme.accent};
  }
  p {
    margin: 18px 0 0;
    font-size: 16px;
    line-height: 1.5;
    color: ${({ theme }) => theme.inkSoft};
    max-width: 52ch;
  }

  @media (max-width: 760px) {
    h1 {
      font-size: 40px;
    }
    p {
      font-size: 15px;
      margin-top: 12px;
    }
  }
`;

const Current = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 46px;
  padding: 0 18px;
  border-radius: 14px;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  font-size: 15px;
  color: ${({ theme }) => theme.inkSoft};

  strong {
    font-weight: 600;
    color: ${({ theme }) => theme.ink};
  }
  i {
    width: 8px;
    height: 8px;
    border-radius: 99px;
    background: ${({ theme, $tone }) => ($tone === "warn" ? theme.warn : theme.success)};
  }
`;

const Toggle = styled.div`
  display: inline-flex;
  padding: 4px;
  border-radius: 16px;
  background: ${({ theme }) => theme.bg2 || theme.bg};
  border: 1px solid ${({ theme }) => theme.line};
`;

const ToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 38px;
  padding: 0 18px;
  border: 0;
  border-radius: 12px;
  font: inherit;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  /* Selected period: a neutral raised pill, like the app's other view switches. */
  color: ${({ theme, $on }) => ($on ? theme.ink : theme.inkMuted)};
  background: ${({ theme, $on }) => ($on ? theme.surface : "transparent")};
  box-shadow: ${({ $on }) => ($on ? "0 1px 3px color-mix(in srgb, #000 14%, transparent), 0 0 0 1px var(--line)" : "none")};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
  }
  span {
    font-size: 12.5px;
    font-weight: 600;
    padding: 3px 9px;
    border-radius: 999px;
    background: ${({ theme }) => theme.secondarySoft};
    color: ${({ theme }) => theme.secondary};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 1280px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

/* A plan is a radio: the whole card selects it and the chosen one gets the blue ring. */
/* Subgrid rows line prices and credits up across cards: keep the 6 in-flow children (name, tagline, price, credits, intro, features). */
const Card = styled.button`
  position: relative;
  display: grid;
  grid-template-rows: subgrid;
  grid-row: span 6;
  row-gap: 0;
  align-content: start;
  text-align: left;
  font: inherit;
  cursor: pointer;
  padding: 26px 28px 28px;
  border-radius: 20px;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme, $selected }) => ($selected ? theme.accent : theme.line)};
  box-shadow: ${({ theme, $selected }) => ($selected ? `0 0 0 3px ${theme.accentSoft}` : "none")};
  color: ${({ theme }) => theme.ink};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 3px;
  }

  @media (max-width: 760px) {
    padding: 20px 18px 22px;
  }
`;

const Radio = styled.span`
  position: absolute;
  top: 26px;
  right: 28px;
  width: 26px;
  height: 26px;
  border-radius: 99px;
  display: grid;
  place-items: center;
  border: 1.5px solid ${({ theme, $on }) => ($on ? theme.accent : theme.lineStrong)};
  background: ${({ theme, $on }) => ($on ? theme.accent : theme.surface)};
  color: ${({ theme }) => theme.onFill};

  @media (max-width: 760px) {
    top: 20px;
    right: 18px;
  }
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 12px;
  padding-right: 40px;
`;

const Name = styled.h2`
  margin: 0;
  font-family: "Instrument Serif", Georgia, serif;
  font-weight: 400;
  font-size: 32px;
  line-height: 1;
  letter-spacing: -0.01em;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 600;
  background: ${({ theme }) => theme.accentSoft};
  color: ${({ theme }) => theme.accent};
`;

const Tagline = styled.p`
  margin: 10px 0 0;
  font-size: 14.5px;
  line-height: 1.5;
  color: ${({ theme }) => theme.inkMuted};
`;

/* Amount, unit and billing note on one line. */
const Price = styled.div`
  margin-top: 22px;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 8px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  .amount {
    font-size: 40px;
    font-weight: 700;
    letter-spacing: -0.03em;
    line-height: 1;
  }
  .per,
  .note {
    font-size: 14px;
    color: ${({ theme }) => theme.inkMuted};
  }
  .note::before {
    content: "·";
    margin-right: 8px;
  }
`;

const Allowance = styled.div`
  margin-top: 22px;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 12px;
  padding: 14px 18px;
  border-radius: 12px;
  background: ${({ theme }) => theme.bg2};

  strong {
    font-family: "Instrument Serif", Georgia, serif;
    font-weight: 400;
    font-size: 32px;
    line-height: 1;
    letter-spacing: -0.01em;
    color: ${({ theme }) => theme.accent};
    font-variant-numeric: tabular-nums;
  }
  span {
    font-size: 11.5px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.inkSoft};
  }
`;

const Intro = styled.div`
  margin-top: 26px;
  font-size: 14px;
  color: ${({ theme }) => theme.inkMuted};
`;

const Features = styled.ul`
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 11px;

  li {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    font-size: 15px;
    line-height: 1.4;
    color: ${({ theme }) => theme.ink2};
  }
  li span {
    flex-shrink: 0;
    margin-top: 2px;
    display: grid;
    place-items: center;
    color: ${({ theme }) => theme.accent};
  }
`;

/* Enterprise sits under the three plans as one white band, styled like the plan cards. */
const Enterprise = styled.div`
  margin-top: 40px;
  padding: 44px 40px;
  border-radius: 24px;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  color: ${({ theme }) => theme.ink};
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 5fr) auto;
  gap: 28px 40px;
  align-items: center;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    align-items: start;
  }
  @media (max-width: 760px) {
    padding: 28px 22px;
  }
`;

const EnterpriseLede = styled.div`
  h2 {
    margin: 0;
    font-family: "Instrument Serif", Georgia, serif;
    font-weight: 400;
    font-size: 40px;
    line-height: 1;
    letter-spacing: -0.01em;
    color: ${({ theme }) => theme.ink};
  }
  h2 em {
    display: block;
    margin-top: 8px;
    font-style: italic;
    font-size: 28px;
    line-height: 1.1;
    color: ${({ theme }) => theme.accent};
  }
  p {
    margin: 16px 0 0;
    font-size: 16px;
    line-height: 1.5;
    color: ${({ theme }) => theme.inkSoft};
  }
`;

const Chips = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  li {
    height: 36px;
    padding: 0 16px;
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    background: ${({ theme }) => theme.bg2};
    font-size: 15px;
    color: ${({ theme }) => theme.ink2};
    white-space: nowrap;
  }
`;

const TalkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 58px;
  padding: 0 28px;
  border-radius: 14px;
  border: 0;
  background: ${({ theme }) => theme.accent};
  color: ${({ theme }) => theme.onFill};
  font: inherit;
  font-size: 18px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.accentHover};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 3px;
  }
`;

/* One panel under the plans: what credits buy, as white cards with a cost pill each, then
   the four charging rules under a hairline. */
const CreditsPanel = styled.section`
  margin-top: 40px;
  padding: 44px 48px 48px;
  border-radius: 24px;
  background: ${({ theme }) => theme.bg2};

  @media (max-width: 760px) {
    padding: 28px 22px 30px;
    border-radius: 18px;
  }
`;

const CreditsHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px 24px;
  flex-wrap: wrap;

  h2 {
    font-family: "Instrument Serif", Georgia, serif;
    font-weight: 400;
    font-size: 52px;
    line-height: 1;
    letter-spacing: -0.015em;
    margin: 0;
    color: ${({ theme }) => theme.ink};
  }
  h2 em {
    font-style: italic;
    color: ${({ theme }) => theme.accent};
  }
  @media (max-width: 760px) {
    h2 {
      font-size: 38px;
    }
  }
`;

const CreditsSub = styled.p`
  margin: 18px 0 0;
  font-size: 15.5px;
  line-height: 1.5;
  color: ${({ theme }) => theme.inkSoft};
  max-width: 72ch;
`;

const UsageLink = styled.button`
  height: 44px;
  padding: 0 22px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.line};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.ink};
  font: inherit;
  font-size: 14.5px;
  font-weight: 500;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    border-color: ${({ theme }) => theme.lineStrong};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
  }
`;

const ToolGrid = styled.div`
  margin-top: 30px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ToolCard = styled.div`
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr);
  column-gap: 22px;
  padding: 22px 26px 24px;
  border-radius: 16px;
  background: ${({ theme }) => theme.surface};

  .icon {
    display: grid;
    place-items: center;
    height: 26px;
    color: ${({ theme }) => theme.ink};
  }
  .title {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 14px;
    min-height: 26px;
  }
  h3 {
    margin: 0;
    font-size: 17.5px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: ${({ theme }) => theme.ink};
  }
  p {
    grid-column: 2;
    margin: 10px 0 0;
    font-size: 15.5px;
    line-height: 1.55;
    color: ${({ theme }) => theme.inkSoft};
  }

  @media (max-width: 760px) {
    padding: 18px 18px 20px;
    column-gap: 14px;
  }
`;

const RuleList = styled.dl`
  margin: 36px 0 0;
  padding: 36px 0 0;
  border-top: 1px solid ${({ theme }) => theme.lineStrong};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 28px 44px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 22px;
  }

  dt {
    margin: 0;
    font-size: 15.5px;
    font-weight: 600;
    color: ${({ theme }) => theme.ink};
  }
  dd {
    margin: 5px 0 0;
    font-size: 15.5px;
    line-height: 1.55;
    color: ${({ theme }) => theme.inkSoft};
    max-width: 70ch;
  }
`;

const CREDIT_TOOLS = [
  {
    icon: FiUserPlus,
    name: "Candidate search",
    cost: "100–500 per 20 people",
    text: "Describe the role and get a ranked shortlist of people. A tight brief sits at the low end.",
  },
  {
    icon: FiAtSign,
    name: "Email lookup",
    cost: "12 credits",
    text: "A verified work email for any candidate or contact. Found or not, one flat price.",
  },
  {
    icon: FiUsers,
    name: "Similar candidates",
    cost: "Varies by run",
    text: "Start from one strong profile and find more like them, minus anyone you already filtered out.",
  },
  {
    icon: FiTarget,
    name: "Job matching",
    cost: "A few credits",
    text: "Candidates scored against your open roles, whether you sourced them or imported them.",
  },
  {
    icon: FiBriefcase,
    name: "Company tracking",
    cost: "A few credits",
    text: "A company's careers page and job boards watched, with new roles in Jobs each morning.",
  },
  {
    icon: FiFileText,
    name: "CV & spreadsheet import",
    cost: "A few credits",
    text: "Resumes and sheets read into profiles, with location and seniority filled in.",
  },
  {
    icon: FiMessageSquare,
    name: "Search assistant",
    cost: "A few credits",
    text: "Tune filters and the brief in chat before a search runs, so credits go on the right people.",
  },
  {
    icon: FiSend,
    name: "Outreach drafts",
    cost: "A few credits",
    text: "A first email from the candidate's profile and the job, ready to edit and send.",
  },
];

const CREDIT_RULES = [
  ["Charged after a run, for what it used", "Nothing is taken up front. Usage shows in Settings under Credits & Usage."],
  [
    "Top up each month on your renewal date",
    "Yearly plans release a month at a time. Starter and Growth reset; Scale rolls unused credits over for three months.",
  ],
  ["At zero, new runs pause", "A run already under way finishes, which can dip the balance slightly below zero."],
  ["Buy extra any time", "Extra credits are spent exactly the same way and never expire."],
];

const Crumb = styled.button`
  appearance: none;
  border: 0;
  background: none;
  padding: 0;
  margin: 0 0 14px;
  font: inherit;
  font-size: 13.5px;
  color: ${({ theme }) => theme.inkMuted};
  cursor: pointer;
  &:hover {
    color: ${({ theme }) => theme.ink};
  }
`;

/* Seats: one row per person, each with its own tier. Pick a row, then a plan card above. */
const SeatPanel = styled.section`
  margin: 28px 0 8px;
  padding: 22px 24px 20px;
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 18px;
  background: ${({ theme }) => theme.surface};

  @media (max-width: 450px) {
    padding: 18px 14px 16px;
  }
`;

const SeatHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 14px;
  h2 {
    margin: 0 0 4px;
    font-size: 20px;
    font-weight: 600;
    letter-spacing: -0.02em;
  }
  p {
    margin: 0;
    font-size: 13.5px;
    color: ${({ theme }) => theme.inkMuted};
    line-height: 1.5;
    max-width: 560px;
  }
`;

const SeatRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SeatRow = styled.div`
  display: grid;
  grid-template-columns: ${({ $plain }) => ($plain ? "minmax(0, 1fr) 150px 110px 32px" : "70px minmax(0, 1fr) 150px 110px 32px")};
  align-items: center;
  gap: 14px;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1.5px solid ${({ theme, $on }) => ($on ? theme.accent : theme.line)};
  background: ${({ theme, $on }) => ($on ? theme.accentSoft : "transparent")};
  cursor: pointer;
  .n {
    font-size: 12.5px;
    font-weight: 600;
    color: ${({ theme }) => theme.inkMuted};
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }
  .who {
    font-size: 14px;
    color: ${({ theme }) => theme.ink};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .who small {
    display: block;
    font-size: 12px;
    color: ${({ theme }) => theme.inkMuted};
  }
  select {
    height: 34px;
    border-radius: 9px;
    border: 1px solid ${({ theme }) => theme.line};
    background: ${({ theme }) => theme.bg};
    color: ${({ theme }) => theme.ink};
    font: inherit;
    font-size: 13.5px;
    padding: 0 10px;
  }
  .price {
    font-size: 13.5px;
    text-align: right;
    font-variant-numeric: tabular-nums;
    color: ${({ theme }) => theme.ink};
  }
  .remove {
    appearance: none;
    border: 0;
    background: none;
    color: ${({ theme }) => theme.inkMuted};
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
    padding: 4px;
    border-radius: 8px;
    &:hover {
      color: var(--danger);
    }
    &:disabled {
      visibility: hidden;
    }
  }
  @media (max-width: 720px) {
    grid-template-columns: ${({ $plain }) => ($plain ? "minmax(0, 1fr) 120px 32px" : "60px minmax(0, 1fr) 120px 32px")};
    .price {
      display: none;
    }
  }
  /* Tighter columns so the name keeps more than ~60px at 375. */
  @media (max-width: 450px) {
    grid-template-columns: ${({ $plain }) => ($plain ? "minmax(0, 1fr) 104px 28px" : "46px minmax(0, 1fr) 104px 28px")};
    gap: 8px;
    padding: 8px 10px;
    .n {
      font-size: 11.5px;
    }
  }
`;

const AddSeat = styled.button`
  margin-top: 14px;
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid ${({ theme }) => theme.line};
  background: ${({ theme }) => theme.bg};
  color: ${({ theme }) => theme.ink};
  border-radius: 999px;
  padding: 8px 16px 8px 12px;
  font: inherit;
  font-size: 13.5px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
  svg {
    color: ${({ theme }) => theme.inkMuted};
  }
  &:hover {
    border-color: ${({ theme }) => theme.ink};
  }
  &:hover svg {
    color: ${({ theme }) => theme.ink};
  }
  &:disabled {
    color: ${({ theme }) => theme.inkMuted};
    border-color: ${({ theme }) => theme.line};
    cursor: default;
  }
`;

/* The order so far, fixed over the bottom of the pane: plan, seats, price, next. Sticky
   can't be used here because html/body set overflow-x hidden. Insets mirror MainContent's
   padding and the sidebar rail offset, so the bar spans exactly the content column. */
const Bar = styled.div`
  position: fixed;
  left: calc(var(--sidebar-offset, 0px) + 2.5rem);
  right: 2.5rem;
  bottom: 16px;
  z-index: 20;
  padding: 14px 22px;
  border-radius: 16px;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.lineStrong};
  box-shadow: 0 18px 40px -24px rgba(8, 24, 44, 0.35);
  transition: left 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 1024px) {
    left: 1.5rem;
    right: 1.5rem;
  }
  @media (max-width: 768px) {
    left: 1rem;
    right: 1rem;
    bottom: 12px;
    padding: 12px 16px;
  }
  @media (max-width: 450px) {
    left: 0.75rem;
    right: 0.75rem;
  }
  @media (max-width: 390px) {
    left: 0.5rem;
    right: 0.5rem;
  }
`;

const BarInner = styled.div`
  zoom: 0.9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 14px 28px;
`;

const Chosen = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.inkMuted};

  strong {
    font-weight: 600;
    color: ${({ theme }) => theme.ink};
  }
`;

const Seats = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13.5px;
  color: ${({ theme }) => theme.inkSoft};
`;

const Stepper = styled.div`
  display: inline-flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.lineStrong};
  border-radius: 999px;
  overflow: hidden;
  background: ${({ theme }) => theme.surface};

  button {
    width: 36px;
    height: 36px;
    border: 0;
    background: transparent;
    font: inherit;
    font-size: 18px;
    color: ${({ theme }) => theme.ink};
    cursor: pointer;
  }
  button:hover {
    background: ${({ theme }) => theme.bg2};
  }
  button:disabled {
    color: ${({ theme }) => theme.inkFaint};
    cursor: default;
  }
  output {
    min-width: 40px;
    text-align: center;
    font-size: 15px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: ${({ theme }) => theme.ink};
  }
`;

const Total = styled.div`
  text-align: right;
  font-size: 12.5px;
  color: ${({ theme }) => theme.inkMuted};
  font-variant-numeric: tabular-nums;

  strong {
    display: block;
    font-size: 22px;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: ${({ theme }) => theme.ink};
  }
`;

const Next = styled.button`
  height: 44px;
  padding: 0 22px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.accent};
  background: ${({ theme }) => theme.accent};
  color: ${({ theme }) => theme.onFill};
  font: inherit;
  font-size: 14.5px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.accentHover};
  }
  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`;

/* Total and the action buttons; on a phone they wrap instead of running off the bar. */
const BarEnd = styled.div`
  display: flex;
  align-items: center;
  gap: 22px;
  margin-left: auto;

  @media (max-width: 450px) {
    flex-wrap: wrap;
    gap: 10px 16px;
    width: 100%;
    ${Total} {
      flex: 1 1 140px;
      text-align: left;
    }
  }
`;

const statusLabel = (s) =>
  ({
    TRIAL: "Trial",
    TRIAL_ENDED: "Trial ended",
    TRIAL_USED: "No plan",
    ACTIVE: "Active",
    UNPAID: "Payment failed",
    CANCELLED: "Cancelled",
  })[s] || "No plan";

const paidPlans = PLANS.filter((p) => p.monthly != null && !p.trial);
const trialPlan = PLANS.find((p) => p.trial);
const enterprise = PLANS.find((p) => p.monthly == null);

export default function PlansPage() {
  const navigate = useNavigate();
  // Parked: with FEATURE_TEAM_SEATS off a plan is one seat, so the seat list stays hidden.
  const { features: appFeatures } = useAuth();
  const multiSeat = Boolean(appFeatures?.teamSeats);
  // On a paid plan the page splits in two: change the seats you have, or add new ones (?view=seats).
  const location = useLocation();
  const [view, setView] = React.useState(() => (new URLSearchParams(location.search).get("view") === "seats" ? "seats" : "plan"));
  // ?seat=<id>: the admin changes one colleague's seat (plan, billing cycle, extra credits).
  const seatParam = React.useMemo(() => new URLSearchParams(location.search).get("seat") || "", [location.search]);
  const [seatHolder, setSeatHolder] = React.useState(null);
  const [yearly, setYearly] = React.useState(false);
  const [status, setStatus] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  // One row per seat: { key, id (existing seat) | null, plan, who, sub, locked }. The first
  // row is the admin's own seat. A seat with someone in it (or an invite out) can change tier
  // but not be removed.
  const [seats, setSeats] = React.useState(() => [{ key: "me", id: null, plan: "growth", who: "You", sub: "", locked: true }]);
  const [focus, setFocus] = React.useState(0);
  const [demoOpen, setDemoOpen] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    Promise.all([
      fetchWithAuth("/api/billing/status").then(async (res) => (res.ok ? res.json() : null)),
      fetchWithAuth("/api/team").then(async (res) => (res.ok ? res.json() : null)).catch(() => null),
    ])
      .then(([body, team]) => {
        if (!alive || !body) return;
        setStatus(body);
        if (body.cycle === "yearly") setYearly(true);
        // Start from what the account already has, so a change is a small step.
        const live = ["ACTIVE", "UNPAID"].includes(body.status) && Array.isArray(body.seatList) && body.seatList.length;
        if (live) {
          const bySeat = new Map((team?.seatList || []).map((x) => [x.id, x]));
          const target = seatParam ? bySeat.get(seatParam) : null;
          if (target) {
            setSeatHolder(target.holder || null);
            setFocus(body.seatList.findIndex((x) => x.id === seatParam));
          }
          setSeats(
            body.seatList.map((x, i) => {
              const t = bySeat.get(x.id);
              const holder = t?.holder;
              const invite = t?.invite;
              return {
                key: x.id,
                id: x.id,
                plan: x.plan,
                who: i === 0 ? "You" : holder ? holder.name || holder.email || "Team member" : invite ? "Invited" : "Empty seat",
                sub: i === 0 ? "" : holder?.name && holder?.email ? holder.email : invite ? invite.email : "Invite someone from Settings",
                locked: i === 0 || Boolean(holder) || Boolean(invite),
              };
            }),
          );
        } else if (body.plan && paidPlans.some((p) => p.id === body.plan)) {
          setSeats([{ key: "me", id: null, plan: body.plan, who: "You", sub: "", locked: true }]);
        }
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const onPaidPlan = status?.plan && ["ACTIVE", "UNPAID"].includes(status.status);
  const isMember = status?.role === "member";
  const seatView = Boolean(onPaidPlan && multiSeat && seatParam && seats.some((s) => s.id === seatParam));
  const tabs = Boolean(onPaidPlan && multiSeat) && !seatView;
  const addingSeats = tabs && view === "seats";
  // "Your plan" edits the admin's own seat (and the billing cycle); colleagues' seats change from Settings → Team.
  const ownPlanView = tabs && !addingSeats;
  React.useEffect(() => {
    if (ownPlanView) setFocus(0);
  }, [ownPlanView]);
  const seatIndex = seatView ? seats.findIndex((s) => s.id === seatParam) : -1;
  React.useEffect(() => {
    if (seatView && seatIndex >= 0) setFocus(seatIndex);
  }, [seatView, seatIndex]);
  const holderName = seatHolder?.name || seatHolder?.email || `Seat ${seatIndex + 1}`;
  const newSeats = seats.filter((s) => !s.id);
  // The add-seats view starts with one new row to configure.
  React.useEffect(() => {
    if (!addingSeats || loading || newSeats.length) return;
    setSeats((rows) => [...rows, { key: `new-${Date.now()}`, id: null, plan: rows[rows.length - 1]?.plan || "growth", who: "Nobody yet", sub: "Invite someone from Settings → Team after paying", locked: false }]);
    setFocus(seats.length);
  }, [addingSeats, loading, newSeats.length, seats.length]);
  const trialDays =
    status?.status === "TRIAL" && status.trialEndsAt ? Math.max(0, Math.ceil((new Date(status.trialEndsAt) - Date.now()) / 86400000)) : null;
  const onTrial = trialDays != null && !onPaidPlan;
  const trialOver = ["TRIAL_ENDED", "TRIAL_USED"].includes(status?.status) && !onPaidPlan;
  const planOf = (id) => paidPlans.find((p) => p.id === id) || paidPlans[1];
  const perSeatMonthly = (id) => (yearly ? Math.round(annualTotal(planOf(id).monthly) / 12) : planOf(id).monthly);
  const plan = planOf(seats[Math.min(focus, seats.length - 1)]?.plan);
  const monthly = seats.reduce((n, s) => n + perSeatMonthly(s.plan), 0);
  const yearlyTotal = seats.reduce((n, s) => n + annualTotal(planOf(s.plan).monthly), 0);
  const tiers = paidPlans.map((p) => [p, seats.filter((s) => s.plan === p.id).length]).filter(([, n]) => n > 0);
  const tierLabel = tiers.map(([p, n]) => `${n}× ${p.name}`).join(", ");
  const setSeatPlan = (i, id) => setSeats((rows) => rows.map((r, k) => (k === i ? { ...r, plan: id } : r)));
  const addSeat = () =>
    setSeats((rows) => {
      if (rows.length >= 50) return rows;
      const next = [...rows, { key: `new-${Date.now()}-${rows.length}`, id: null, plan: plan.id, who: "Nobody yet", sub: "Invite someone from Settings → Team after paying", locked: false }];
      setFocus(next.length - 1);
      return next;
    });
  const removeSeat = (i) =>
    setSeats((rows) => {
      const next = rows.filter((_, k) => k !== i);
      setFocus((f) => Math.min(f, next.length - 1));
      return next;
    });
  // The URL carries every seat: an existing seat by id, a new one as "new".
  const seatsParam = seats.map((s) => `${s.id || "new"}:${s.plan}`).join(",");
  const unchanged = addingSeats
    ? newSeats.length === 0
    : onPaidPlan &&
      (status.cycle || "monthly") === (yearly ? "yearly" : "monthly") &&
      seats.length === (status.seatList || []).length &&
      seats.every((s, i) => s.id && status.seatList[i]?.id === s.id && status.seatList[i]?.plan === s.plan);
  // Which rows this view edits: new seats only when adding, existing seats only when changing.
  const visibleRows = seats.map((s, i) => [s, i]).filter(([s]) => (tabs ? (addingSeats ? !s.id : Boolean(s.id)) : true));
  const newMonthly = newSeats.reduce((n, s) => n + perSeatMonthly(s.plan), 0);
  const labelOf = (rows) => paidPlans.map((p) => [p, rows.filter((s) => s.plan === p.id).length]).filter(([, n]) => n > 0).map(([p, n]) => `${n}× ${p.name}`).join(", ");
  const newTierLabel = labelOf(newSeats);
  const keptTierLabel = labelOf(seats.filter((s) => s.id));

  return (
    <Page>
      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
      <Scaled>
        {seatView && (
          <Crumb type="button" onClick={() => navigate("/settings?tab=team")}>
            ← Back to Team
          </Crumb>
        )}
        <Head>
          <div>
            <h1>
              {seatView ? (
                <>
                  Plan for <em>{holderName}.</em>
                </>
              ) : addingSeats ? (
                <>
                  Add <em>seats.</em>
                </>
              ) : tabs ? (
                <>
                  Change your <em>plan.</em>
                </>
              ) : (
                <>
                  Pick your <em>plan.</em>
                </>
              )}
            </h1>
            <p>
              {seatView
                ? `Seat ${seatIndex + 1} of your plan. Pick its plan below, switch how the whole plan is billed, or add credits to ${seatHolder ? "their" : "this seat's"} balance.`
                : addingSeats
                ? "Each new seat is one more person, with its own plan and its own credits. Invite them from Settings → Team once it's paid."
                : ownPlanView
                  ? "Your own seat, and how the whole plan is billed. A colleague's seat changes from Settings → Team."
                  : "Credits top up every month and pay for every tool alike. Change or cancel any time from Settings."}
            </p>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            {/* The chip only earns its place with something to say, such as a failed payment; the trial card says the trial's own state. */}
            {!(status?.status === "ACTIVE" && onPaidPlan) && !onTrial && !trialOver && (
            <div style={addingSeats ? { visibility: "hidden" } : undefined} aria-hidden={addingSeats || undefined}>
            {loading ? (
              <Skeleton width={180} height={46} inline />
            ) : (
              <Current $tone={status?.status === "TRIAL_ENDED" || status?.status === "UNPAID" ? "warn" : "ok"}>
                <i />
                {onPaidPlan ? (
                  <>
                    <strong>{status.planName}</strong> {statusLabel(status.status).toLowerCase()}
                    {Number(status.seats) > 1 && `, ${status.seats} seats`}
                  </>
                ) : (
                  <strong>{statusLabel(status?.status)}</strong>
                )}
              </Current>
            )}
            </div>
            )}
            <Toggle role="group" aria-label="Billing period" style={addingSeats ? { visibility: "hidden" } : undefined} aria-hidden={addingSeats || undefined}>
              <ToggleButton type="button" $on={!yearly} aria-pressed={!yearly} onClick={() => setYearly(false)}>
                Monthly
              </ToggleButton>
              <ToggleButton type="button" $on={yearly} aria-pressed={yearly} onClick={() => setYearly(true)}>
                Yearly <span>Save {Math.round(ANNUAL_DISCOUNT * 100)}%</span>
              </ToggleButton>
            </Toggle>
            {tabs && (
              <Toggle role="tablist" aria-label="What to change">
                <ToggleButton type="button" role="tab" $on={!addingSeats} aria-selected={!addingSeats} onClick={() => setView("plan")}>
                  Change plan
                </ToggleButton>
                <ToggleButton type="button" role="tab" $on={addingSeats} aria-selected={addingSeats} onClick={() => setView("seats")}>
                  Add seats
                </ToggleButton>
              </Toggle>
            )}
          </div>
        </Head>

        <Grid role="radiogroup" aria-label="Plan">
          {trialPlan && !addingSeats && !seatView && (
            /* The free trial: shown for comparison, never bought, so not one of the radios. */
            <Card as="div" style={{ cursor: "default" }}>
              <NameRow>
                <Name>{trialPlan.name}</Name>
                {onTrial ? (
                  <Tag>{trialDays === 0 ? "Your plan · ends today" : `Your plan · ${trialDays} day${trialDays === 1 ? "" : "s"} left`}</Tag>
                ) : trialOver ? (
                  <Tag>Trial ended</Tag>
                ) : null}
              </NameRow>
              <Tagline>{trialPlan.tagline}</Tagline>
              <Price>
                <span className="amount">{usd(0)}</span>
                <span className="per">for {TRIAL_DAYS} days</span>
                <span className="note">No card needed</span>
              </Price>
              <Allowance>
                <strong>{formatCredits(trialPlan.credits)}</strong>
                <span>credits to start</span>
              </Allowance>
              <Intro>What's included</Intro>
              <Features>
                {trialPlan.features.map((f) => (
                  <li key={f}>
                    <span>
                      <FiCheck size={14} strokeWidth={2.5} />
                    </span>
                    {f}
                  </li>
                ))}
              </Features>
            </Card>
          )}
          {paidPlans.map((p) => {
            const isCurrent = onPaidPlan && status.plan === p.id;
            const isSelected = p.id === plan.id;
            const shown = yearly ? Math.round(annualTotal(p.monthly) / 12) : p.monthly;
            const focusIdx = Math.min(focus, seats.length - 1);
            const seatNo = addingSeats ? newSeats.findIndex((x) => x === seats[focusIdx]) + 1 : focusIdx + 1;
            return (
              <Card key={p.id} type="button" role="radio" aria-checked={isSelected} onClick={() => setSeatPlan(Math.min(focus, seats.length - 1), p.id)} $selected={isSelected}>
                <Radio $on={isSelected}>{isSelected && <FiCheck size={14} strokeWidth={3} />}</Radio>
                <NameRow>
                  <Name>{p.name}</Name>
                  {isSelected && multiSeat && seats.length > 1 && !ownPlanView && !seatView ? <Tag>{addingSeats ? `New seat ${seatNo}` : `Seat ${seatNo}`}</Tag> : isCurrent ? <Tag>{multiSeat ? "Your seat" : "Your plan"}</Tag> : p.featured ? <Tag>Most popular</Tag> : null}
                </NameRow>
                <Tagline>{p.tagline}</Tagline>
                <Price>
                  <span className="amount">{usd(shown)}</span>
                  <span className="per">/seat/month</span>
                  <span className="note">{yearly ? `${usd(annualTotal(p.monthly))}/year` : "Billed monthly"}</span>
                </Price>
                <Allowance>
                  <strong>{formatCredits(p.credits)}</strong>
                  <span>credits/month per seat</span>
                </Allowance>
                <Intro>{p.intro || "What's included"}</Intro>
                <Features>
                  {p.features.map((f) => (
                    <li key={f}>
                      <span>
                        <FiCheck size={14} strokeWidth={2.5} />
                      </span>
                      {f}
                    </li>
                  ))}
                </Features>
              </Card>
            );
          })}
        </Grid>

        {multiSeat && !ownPlanView && !seatView && (
        <SeatPanel aria-labelledby="your-seats">
          <SeatHead>
            <div>
              <h2 id="your-seats">{addingSeats ? "New seats" : "Your seats"}</h2>
              <p>
                {addingSeats
                  ? `Keeping your ${seats.filter((s) => s.id).length} seat${seats.filter((s) => s.id).length === 1 ? "" : "s"} as ${seats.filter((s) => s.id).length === 1 ? "it is" : "they are"} (${keptTierLabel}). Pick a new seat here, then choose its plan above.`
                  : "One seat per person, each with its own plan and its own credits every month. Pick a seat here, then choose its plan above."}
                {!addingSeats && tabs ? " To add or drop seats, use the Add seats tab; a seat with someone in it can change plan but not be removed." : ""}
              </p>
            </div>
          </SeatHead>
          <SeatRows role="listbox" aria-label="Seats">
            {visibleRows.map(([s, i], k) => (
              <SeatRow key={s.key} role="option" aria-selected={i === focus} $on={i === focus} $plain={addingSeats} onClick={() => setFocus(i)}>
                {!addingSeats && <span className="n">Seat {i + 1}</span>}
                <span className="who">
                  {addingSeats ? `New seat ${k + 1}` : s.who}
                  {s.sub && <small>{s.sub}</small>}
                </span>
                <select value={s.plan} aria-label={`Plan for seat ${i + 1}`} onClick={(e) => e.stopPropagation()} onChange={(e) => setSeatPlan(i, e.target.value)}>
                  {paidPlans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                <span className="price">{usd(perSeatMonthly(s.plan))}/month</span>
                <button
                  type="button"
                  className="remove"
                  aria-label={`Remove seat ${i + 1}`}
                  disabled={s.locked || (tabs && !addingSeats)}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeSeat(i);
                  }}
                >
                  ×
                </button>
              </SeatRow>
            ))}
          </SeatRows>
          {(!tabs || addingSeats) && (
            <AddSeat type="button" onClick={addSeat} disabled={seats.length >= 50 || isMember}>
              <FiPlus size={15} strokeWidth={2.5} />
              Add a seat
            </AddSeat>
          )}
        </SeatPanel>
        )}

        {!addingSeats && enterprise && (
          <Enterprise>
            <EnterpriseLede>
              <h2>
                {enterprise.name}
                <em>credits on your terms</em>
              </h2>
              <p>
                {enterprise.tagline} {enterprise.intro ? `${enterprise.intro}:` : ""}
              </p>
            </EnterpriseLede>
            <Chips>
              {enterprise.features.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </Chips>
            <TalkButton type="button" onClick={() => setDemoOpen(true)}>
              Talk to us <FiArrowRight size={18} />
            </TalkButton>
          </Enterprise>
        )}

        {!addingSeats && (
        <CreditsPanel aria-labelledby="what-are-credits">
          <CreditsHead>
            <div>
              <h2 id="what-are-credits">
                What are <em>credits?</em>
              </h2>
              <CreditsSub>One balance for every tool in the app. You're charged after each run, for what it actually used.</CreditsSub>
            </div>
            <UsageLink type="button" onClick={() => navigate("/settings?tab=credits")}>
              Credits &amp; Usage
            </UsageLink>
          </CreditsHead>
          <ToolGrid>
            {CREDIT_TOOLS.map(({ icon: Icon, name, text }) => (
              <ToolCard key={name}>
                <span className="icon">
                  <Icon size={22} strokeWidth={1.75} />
                </span>
                <div className="title">
                  <h3>{name}</h3>
                </div>
                <p>{text}</p>
              </ToolCard>
            ))}
          </ToolGrid>
          <RuleList>
            {CREDIT_RULES.map(([lead, text]) => (
              <div key={lead}>
                <dt>{lead}</dt>
                <dd>{text}</dd>
              </div>
            ))}
          </RuleList>
        </CreditsPanel>
        )}
      </Scaled>

      <Bar>
        <BarInner>
          <Chosen>
            {seatView ? (
              <>
                <strong>{plan.name}</strong> for {holderName}, {yearly ? "yearly" : "monthly"}
              </>
            ) : addingSeats ? (
              <>
                <strong>
                  Adding {newSeats.length} seat{newSeats.length === 1 ? "" : "s"}
                </strong>
                {newTierLabel ? ` · ${newTierLabel}` : ""} · {seats.length} in total
              </>
            ) : ownPlanView ? (
              <>
                <strong>{plan.name}</strong> for your seat, {yearly ? "yearly" : "monthly"}
                {seats.length > 1 ? ` · ${seats.length} seats in total` : ""}
              </>
            ) : multiSeat ? (
              <>
                <strong>
                  {seats.length} seat{seats.length === 1 ? "" : "s"}
                </strong>
                , {yearly ? "yearly" : "monthly"} · {tierLabel}
              </>
            ) : (
              <>
                <strong>{plan.name}</strong>, {yearly ? "yearly" : "monthly"} · {usd(perSeatMonthly(plan.id))}/month
              </>
            )}
          </Chosen>
          {multiSeat && !seatView && (!tabs || addingSeats) && (
          <Seats>
            Seats
            <Stepper>
              <button
                type="button"
                onClick={() => removeSeat(seats.length - 1)}
                disabled={addingSeats ? newSeats.length === 0 : seats.length <= 1 || seats[seats.length - 1]?.locked}
                aria-label="Fewer seats"
              >
                −
              </button>
              <output>{addingSeats ? newSeats.length : seats.length}</output>
              <button type="button" onClick={addSeat} disabled={seats.length >= 50} aria-label="More seats">
                +
              </button>
            </Stepper>
          </Seats>
          )}
          <BarEnd>
            <Total>
              {addingSeats ? (
                <>
                  <strong>+{usd(newMonthly)}/month</strong>
                  {usd(monthly)}/month in total{yearly ? `, ${usd(yearlyTotal)} billed yearly` : ""}
                </>
              ) : (
                <>
                  <strong>{usd(monthly)}/month</strong>
                  {yearly ? `${usd(yearlyTotal)} billed yearly · ` : ""}
                  {tiers.map(([p, n]) => `${formatCredits(p.credits)} credits/month per ${p.name} seat`).join(" · ")}
                </>
              )}
            </Total>
            {seatView && seatHolder?.userId && (
              <Next
                type="button"
                style={{ background: "transparent", color: "inherit", border: "1px solid currentColor" }}
                onClick={() => navigate(`/checkout?topup=1&for=${encodeURIComponent(seatHolder.userId)}&forName=${encodeURIComponent(holderName)}`)}
              >
                Add credits for {seatHolder.name ? seatHolder.name.split(" ")[0] : "them"}
              </Next>
            )}
            <Next
              type="button"
              disabled={unchanged || isMember}
              onClick={() =>
                navigate(
                  `/checkout?${onPaidPlan ? "change=1&" : ""}plan=${seats[0]?.plan || "growth"}&cycle=${yearly ? "yearly" : "monthly"}&seats=${encodeURIComponent(seatsParam)}${seatView ? `&back=team&seat=${encodeURIComponent(seatParam)}&forName=${encodeURIComponent(holderName)}` : ""}`,
                )
              }
            >
              {isMember ? "Managed by your admin" : addingSeats ? (unchanged ? "Add a seat first" : "Review new seats") : unchanged ? (seatView ? "No change yet" : "Current plan") : onPaidPlan ? "Review change" : "Next: add-ons"}
            </Next>
          </BarEnd>
        </BarInner>
      </Bar>
    </Page>
  );
}
